import { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc, deleteDoc, query, where } from 'firebase/firestore';
import { auth, db } from '../../services/firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import AdminLayout from '../../components/admin/AdminLayout';
import { ALL_TEAMS } from '../../utils/helpers';
import { useClub } from '../../contexts/ClubContext';

function ManageCoaches() {
  const [user] = useAuthState(auth);
  const { clubId, club } = useClub();
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [newCoach, setNewCoach] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    team: ''
  });

  // Fetch coaches (filtered by clubId)
  useEffect(() => {
    const fetchCoaches = async () => {
      if (!clubId) return;
      try {
        const q = query(
          collection(db, 'users'), 
          where('role', '==', 'coach'),
          where('clubId', '==', clubId)
        );
        const snapshot = await getDocs(q);
        const coachList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setCoaches(coachList);
      } catch (error) {
        console.error('Error fetching coaches:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCoaches();
  }, [clubId]);

  const handleAddCoach = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      // Check if a user with this email already exists
      const existingUserQuery = query(
        collection(db, 'users'),
        where('email', '==', newCoach.email.toLowerCase())
      );
      const existingUsers = await getDocs(existingUserQuery);
      
      let coachId;
      let coachData;
      
      if (!existingUsers.empty) {
        // User exists - update their role to coach
        const existingUser = existingUsers.docs[0];
        coachId = existingUser.id;
        const existingData = existingUser.data();
        
        // Check if they're already assigned to another club
        if (existingData.clubId && existingData.clubId !== clubId) {
          setError(`This user is already assigned to another club (${existingData.clubId}). They must leave that club first.`);
          setSaving(false);
          return;
        }
        
        // Update existing user to coach role
        coachData = {
          ...existingData,
          firstName: newCoach.firstName || existingData.firstName,
          lastName: newCoach.lastName || existingData.lastName,
          displayName: `${newCoach.firstName} ${newCoach.lastName}`,
          phone: newCoach.phone || existingData.phone,
          team: newCoach.team,
          role: 'coach',
          status: 'active',
          clubId: clubId,
          updatedAt: new Date(),
          updatedBy: user?.uid
        };
        
        await setDoc(doc(db, 'users', coachId), coachData);
        setSuccess(`${newCoach.firstName} ${newCoach.lastName} has been made a coach! They can now log in with their existing account.`);
      } else {
        // User doesn't exist - create a pending coach invite
        coachId = `coach_invite_${Date.now()}`;
        coachData = {
          firstName: newCoach.firstName,
          lastName: newCoach.lastName,
          displayName: `${newCoach.firstName} ${newCoach.lastName}`,
          email: newCoach.email.toLowerCase(),
          phone: newCoach.phone,
          team: newCoach.team,
          role: 'coach',
          status: 'invited', // They need to create an account
          clubId: clubId,
          createdAt: new Date(),
          createdBy: user?.uid
        };
        
        await setDoc(doc(db, 'users', coachId), coachData);
        setSuccess(`Invite created for ${newCoach.firstName} ${newCoach.lastName}. They need to sign up with email: ${newCoach.email}`);
      }

      setCoaches([...coaches, { id: coachId, ...coachData }]);
      setNewCoach({ firstName: '', lastName: '', email: '', phone: '', team: '' });
      
      setTimeout(() => {
        setShowAddModal(false);
        setSuccess('');
      }, 2500);

    } catch (error) {
      console.error('Error adding coach:', error);
      setError('Failed to add coach. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCoach = async (coachId, coachName) => {
    if (!window.confirm(`Are you sure you want to remove ${coachName}?`)) return;
    
    try {
      await deleteDoc(doc(db, 'users', coachId));
      setCoaches(coaches.filter(c => c.id !== coachId));
    } catch (error) {
      console.error('Error deleting coach:', error);
    }
  };

  return (
    <AdminLayout title="Manage Coaches" subtitle={`Add and manage coaching staff for ${club?.name || 'your club'}`}>
      <div className="admin-page-header">
        <div></div>
        <button className="btn-add" onClick={() => setShowAddModal(true)}>
          + Add Coach
        </button>
      </div>

      {/* Coaches Table */}
      <div className="admin-card">
        <div className="card-content">
          {loading ? (
            <p className="loading-text">Loading coaches...</p>
          ) : coaches.length === 0 ? (
            <div className="empty-state">
              <span className="empty-icon">🏃</span>
              <h3>No Coaches Yet</h3>
              <p>Add your first coach to get started.</p>
              <button className="btn-add" onClick={() => setShowAddModal(true)}>
                + Add Coach
              </button>
            </div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Team</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {coaches.map(coach => (
                  <tr key={coach.id}>
                    <td>{coach.firstName} {coach.lastName}</td>
                    <td>{coach.email}</td>
                    <td>{coach.phone || '-'}</td>
                    <td><span className="badge">{coach.team || '-'}</span></td>
                    <td>
                      <span className={`status-badge ${coach.status || 'pending'}`}>
                        {coach.status || 'Pending'}
                      </span>
                    </td>
                    <td>
                      <button 
                        className="btn-delete"
                        onClick={() => handleDeleteCoach(coach.id, `${coach.firstName} ${coach.lastName}`)}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Add Coach Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add New Coach</h2>
              <button className="modal-close" onClick={() => setShowAddModal(false)}>×</button>
            </div>
            <form onSubmit={handleAddCoach}>
              <div className="modal-body">
                {error && <div className="form-error">{error}</div>}
                {success && <div className="form-success">{success}</div>}
                
                <div className="form-row">
                  <div className="form-group">
                    <label>First Name *</label>
                    <input
                      type="text"
                      value={newCoach.firstName}
                      onChange={e => setNewCoach({...newCoach, firstName: e.target.value})}
                      placeholder="Enter first name"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Last Name *</label>
                    <input
                      type="text"
                      value={newCoach.lastName}
                      onChange={e => setNewCoach({...newCoach, lastName: e.target.value})}
                      placeholder="Enter last name"
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={newCoach.email}
                    onChange={e => setNewCoach({...newCoach, email: e.target.value})}
                    placeholder="coach@example.com"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={newCoach.phone}
                    onChange={e => setNewCoach({...newCoach, phone: e.target.value})}
                    placeholder="(868) 123-4567"
                  />
                </div>

                <div className="form-group">
                  <label>Assigned Team</label>
                  <select
                    value={newCoach.team}
                    onChange={e => setNewCoach({...newCoach, team: e.target.value})}
                  >
                    <option value="">Select a team...</option>
                    {ALL_TEAMS.map(team => (
                      <option key={team} value={team}>{team}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={() => setShowAddModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-save" disabled={saving}>
                  {saving ? 'Adding...' : 'Add Coach'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default ManageCoaches;
